import React, { useEffect, useState } from "react";
import { PlusIcon, TrashIcon, CheckCircleIcon, ExclamationTriangleIcon } from "@heroicons/react/24/solid";
import { getAdvisorProfile, updateAdvisorProfile } from "../../api/advisorApi";

// Same look-and-feel kit Settings.jsx uses, locally redeclared so this page
// doesn't reach across page boundaries for shared primitives.
const Card = ({ children, className = "" }) => (
  <div
    className={`p-5 sm:p-6 rounded-2xl bg-white/60 dark:bg-[#1a2332]/60 backdrop-blur-xl border border-white/40 dark:border-white/10 shadow-glass dark:shadow-glass-dark ${className}`}
  >
    {children}
  </div>
);

const Field = ({ label, hint, children }) => (
  <label className="block">
    <div className="text-xs font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400 mb-1.5">
      {label}
    </div>
    {children}
    {hint && <p className="text-[11px] text-gray-500 mt-1">{hint}</p>}
  </label>
);

const inputCls =
  "w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 outline-none focus:ring-2 focus:ring-indigo-500";

const TAX_JURISDICTIONS = [
  { value: "", label: "Not set" },
  { value: "IN", label: "🇮🇳 India" },
  { value: "US", label: "🇺🇸 United States" },
  { value: "GB", label: "🇬🇧 United Kingdom" },
  { value: "CA", label: "🇨🇦 Canada" },
  { value: "AU", label: "🇦🇺 Australia" },
  { value: "SG", label: "🇸🇬 Singapore" },
  { value: "AE", label: "🇦🇪 UAE" },
];

const StatusBanner = ({ kind, children }) => {
  const cls =
    kind === "error"
      ? "bg-red-500/10 text-red-500 border-red-500/40"
      : "bg-emerald-500/10 text-emerald-500 border-emerald-500/40";
  const Icon = kind === "error" ? ExclamationTriangleIcon : CheckCircleIcon;
  return (
    <div className={`flex items-start gap-2 p-3 rounded-lg border text-sm ${cls}`}>
      <Icon className="w-4 h-4 mt-0.5 shrink-0" />
      <div className="flex-1">{children}</div>
    </div>
  );
};

const AdvisorProfileEditor = () => {
  const [advisor, setAdvisor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null); // {kind:'success'|'error', message}

  useEffect(() => {
    (async () => {
      try {
        setAdvisor(await getAdvisorProfile());
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading || !advisor) return <Card>Loading…</Card>;

  const flash = (kind, message) => {
    setStatus({ kind, message });
    setTimeout(() => setStatus(null), 4000);
  };

  const patch = async (payload) => {
    try {
      const updated = await updateAdvisorProfile(payload);
      setAdvisor(updated);
      flash("success", "Saved.");
    } catch (err) {
      const data = err?.response?.data || {};
      const firstKey = Object.keys(data)[0];
      const msg = firstKey
        ? `${firstKey}: ${Array.isArray(data[firstKey]) ? data[firstKey][0] : data[firstKey]}`
        : "Could not save.";
      flash("error", msg);
    }
  };

  const updateLocal = (k, v) => setAdvisor((a) => ({ ...a, [k]: v }));

  const setRow = (key, i, field, value) => {
    const next = [...(advisor[key] || [])];
    next[i] = { ...next[i], [field]: value };
    updateLocal(key, next);
  };
  const addRow = (key, empty) => updateLocal(key, [...(advisor[key] || []), empty]);
  const removeRow = (key, i) => {
    const next = [...(advisor[key] || [])];
    next.splice(i, 1);
    updateLocal(key, next);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Advisor profile</h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          This is what subscribers see. Your account email and personal name stay private —
          only what's on this page reaches the marketplace.
        </p>
      </div>

      {status && <StatusBanner kind={status.kind}>{status.message}</StatusBanner>}

      <Card>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Display name" hint="Shown publicly. Keep it brief.">
            <input
              className={inputCls}
              value={advisor.display_name || ""}
              onChange={(e) => updateLocal("display_name", e.target.value)}
              onBlur={() => patch({ display_name: advisor.display_name })}
              maxLength={120}
            />
          </Field>
          <Field label="Jurisdiction" hint="Where you're licensed / based.">
            <select
              className={inputCls}
              value={advisor.jurisdiction || ""}
              onChange={(e) => {
                updateLocal("jurisdiction", e.target.value);
                patch({ jurisdiction: e.target.value });
              }}
            >
              {TAX_JURISDICTIONS.map((j) => (
                <option key={j.value || "_blank"} value={j.value}>{j.label}</option>
              ))}
            </select>
          </Field>
        </div>
        <div className="mt-4">
          <Field label="Headline" hint="One-line pitch. Shown on the marketplace card.">
            <input
              className={inputCls}
              value={advisor.headline || ""}
              onChange={(e) => updateLocal("headline", e.target.value)}
              onBlur={() => patch({ headline: advisor.headline })}
              maxLength={200}
              placeholder="e.g. Bottom-up value picker. 12 years across small-cap India."
            />
          </Field>
        </div>
        <div className="mt-4">
          <Field label="Bio" hint="A few paragraphs about your approach, background, and philosophy.">
            <textarea
              className={inputCls}
              rows={5}
              value={advisor.bio || ""}
              onChange={(e) => updateLocal("bio", e.target.value)}
              onBlur={() => patch({ bio: advisor.bio })}
            />
          </Field>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
          <Field label="Headshot URL" hint="Square photo. Hosted anywhere — we proxy it client-side.">
            <input
              className={inputCls}
              value={advisor.headshot_url || ""}
              onChange={(e) => updateLocal("headshot_url", e.target.value)}
              onBlur={() => patch({ headshot_url: advisor.headshot_url })}
              placeholder="https://…"
            />
          </Field>
          <Field label="Intro video URL" hint="YouTube / Vimeo / direct mp4. 60-90 seconds is best.">
            <input
              className={inputCls}
              value={advisor.intro_video_url || ""}
              onChange={(e) => updateLocal("intro_video_url", e.target.value)}
              onBlur={() => patch({ intro_video_url: advisor.intro_video_url })}
              placeholder="https://…"
            />
          </Field>
        </div>
      </Card>

      <Card>
        <h2 className="text-lg font-bold mb-1">Qualifications</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Designations, licenses, education. Marked unverified until our review team confirms.
        </p>
        <div className="space-y-3">
          {(advisor.qualifications || []).map((q, i) => (
            <div key={i} className="flex gap-2">
              <input
                className={inputCls}
                value={q.label || ""}
                onChange={(e) => setRow("qualifications", i, "label", e.target.value)}
                onBlur={() => patch({ qualifications: advisor.qualifications })}
                placeholder="e.g. CFA Level III, SEBI-RA INH000012345"
              />
              <button
                type="button"
                onClick={() => {
                  removeRow("qualifications", i);
                  patch({
                    qualifications: (advisor.qualifications || []).filter((_, idx) => idx !== i),
                  });
                }}
                className="w-10 h-10 rounded-lg flex items-center justify-center text-red-500 hover:bg-red-500/10 shrink-0"
                title="Remove"
              >
                <TrashIcon className="w-4 h-4" />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => addRow("qualifications", { label: "", verified: false })}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-indigo-500/40 bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 text-sm font-semibold hover:bg-indigo-500/20"
          >
            <PlusIcon className="w-4 h-4" />
            Add qualification
          </button>
        </div>
      </Card>

      <Card>
        <h2 className="text-lg font-bold mb-1">Achievements</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Awards, prior fund management, notable calls. Year is optional.
        </p>
        <div className="space-y-3">
          {(advisor.achievements || []).map((a, i) => (
            <div key={i} className="flex gap-2">
              <input
                className={`${inputCls} flex-1`}
                value={a.title || ""}
                onChange={(e) => setRow("achievements", i, "title", e.target.value)}
                onBlur={() => patch({ achievements: advisor.achievements })}
                placeholder="e.g. Beat Nifty 500 TRI by 9% in FY24"
              />
              <input
                className={`${inputCls} w-24`}
                value={a.year || ""}
                onChange={(e) => setRow("achievements", i, "year", e.target.value)}
                onBlur={() => patch({ achievements: advisor.achievements })}
                placeholder="Year"
              />
              <button
                type="button"
                onClick={() => {
                  removeRow("achievements", i);
                  patch({
                    achievements: (advisor.achievements || []).filter((_, idx) => idx !== i),
                  });
                }}
                className="w-10 h-10 rounded-lg flex items-center justify-center text-red-500 hover:bg-red-500/10 shrink-0"
                title="Remove"
              >
                <TrashIcon className="w-4 h-4" />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => addRow("achievements", { title: "", year: "" })}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-indigo-500/40 bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 text-sm font-semibold hover:bg-indigo-500/20"
          >
            <PlusIcon className="w-4 h-4" />
            Add achievement
          </button>
        </div>
      </Card>
    </div>
  );
};

export default AdvisorProfileEditor;
